import {
  Component,
  Input,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Chart, ChartConfiguration, ChartDataset } from 'chart.js/auto';
import { TranslocoService } from '@jsverse/transloco';
import { ColorsService } from '../../services/colors.service';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';

interface ChartData {
  label: string;
  value: number;
  color: string;
  title?: string;
}

interface DoughnutDataset extends ChartDataset<'doughnut'> {
  hoverOffset?: number;
  hoverBorderWidth?: number;
}

@Component({
  selector: 'app-chart-doughnut',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="chart-container"
      [style.width.px]="getResponsiveContainerSize()"
      [style.height.px]="getResponsiveContainerSize()"
      style="position: static; overflow: visible; display: flex; align-items: center; justify-content: center;"
    >
      <canvas #chartCanvas [width]="size" [height]="size"></canvas>
    </div>
  `,
  styles: [],
})
export class ChartDoughnutComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @ViewChild('chartCanvas', { static: true })
  chartCanvas!: ElementRef<HTMLCanvasElement>;

  @Input() data: ChartData[] = [];
  @Input() size: number = 256;
  @Input() centerValue?: string;
  @Input() centerLabel?: string;
  @Input() textSize?: 'small';
  @Input() thickness: number = 40; // Épaisseur du donut
  @Input() showLabelInTooltip: boolean = true;

  getResponsivePadding(): number {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width < 640) return 0; // Mobile - pas de padding pour utiliser toute la largeur
      if (width < 1024) return 20; // Tablet - padding réduit
      return 20; // Desktop - padding minimal pour optimiser l'espace
    }
    return 20; // Fallback
  }

  getResponsiveContainerSize(): number {
    const padding = this.getResponsivePadding();
    return this.size + padding * 2;
  }

  chart?: Chart; // Public to allow access from parent
  private transloco = inject(TranslocoService);
  private colorsService = inject(ColorsService);
  private formatCo2e = inject(FormatCo2eService);

  ngAfterViewInit(): void {
    this.createChart();
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  // No custom external tooltip (reverted to internal Chart.js tooltips)

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.updateData(this.data);
    }
    if (
      (changes['centerValue'] && !changes['centerValue'].firstChange) ||
      (changes['centerLabel'] && !changes['centerLabel'].firstChange)
    ) {
      // Le plugin centerText lit les inputs au draw : forcer un redraw
      this.chart?.update('none');
    }
    if (
      (changes['size'] && !changes['size'].firstChange) ||
      (changes['thickness'] && !changes['thickness'].firstChange)
    ) {
      // Recreate chart if geometry changes
      if (this.chart) {
        this.chart.destroy();
      }
      this.createChart();
    }
  }

  private get centerTextSize(): {
    value: number;
    label: number;
    firstLineSpacing: number;
    secondLineSpacing: number;
  } {
    return {
      value: this.textSize === 'small' ? 12 : 32,
      label: this.textSize === 'small' ? 10 : 18,
      firstLineSpacing: this.textSize === 'small' ? 6 : 12,
      secondLineSpacing: this.textSize === 'small' ? 6 : 18,
    };
  }

  private createChart(): void {
    if (!this.chartCanvas?.nativeElement) {
      return;
    }

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    // If no data or all values are zero, create a gray placeholder chart
    const hasNonZeroData = this.data.some((item) => item.value > 0);
    const isEmptyState = this.data.length === 0 || !hasNonZeroData;
    const chartData = isEmptyState
      ? [{ label: '', value: 100, color: this.colorsService.EMPTY_COLOR }]
      : this.data;

    const cutoutSize = this.size / 2 - this.thickness;
    const cutoutPercentage = Math.round((cutoutSize / (this.size / 2)) * 100);

    // Plugin pour dessiner le texte central
    const centerTextPlugin = {
      id: 'centerText',
      beforeDraw: (chart: Chart) => {
        // Always display center text if provided (even for zero emissions)
        if (!this.centerValue || !this.centerLabel) return;

        const { ctx, chartArea } = chart;
        const { left, right, top, bottom } = chartArea;

        ctx.save();

        // Couleur primaire du thème (commenté car non utilisé pour le moment)
        // const primaryColor = getComputedStyle(document.documentElement)
        //   .getPropertyValue('--color-primary-500')
        //   .trim() || '#527A6D';

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;

        // Texte principal (valeur) - plus grand et en gras
        ctx.fillStyle = '#1f2937'; // text-greyscale-900 equivalent
        ctx.font = `bold ${this.centerTextSize.value}px sans-serif`; // équivalent text-4xl
        ctx.fillText(
          this.centerValue,
          centerX,
          centerY - this.centerTextSize.firstLineSpacing,
        );

        // Texte secondaire (label) - plus petit et gris
        ctx.fillStyle = '#6b7280'; // text-greyscale-600 equivalent
        ctx.font = `${this.centerTextSize.label}px sans-serif`; // équivalent text-lg
        ctx.fillText(
          this.centerLabel,
          centerX,
          centerY + this.centerTextSize.secondLineSpacing,
        );

        ctx.restore();
      },
    };

    const config: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: {
        labels: chartData.map((item) => item.label),
        datasets: [
          {
            data: chartData.map((item) => item.value),
            backgroundColor: chartData.map((item) => item.color),
            borderWidth: 0,
            hoverOffset: isEmptyState ? 0 : 8, // Pas de hover en mode empty
            hoverBorderWidth: isEmptyState ? 0 : 2,
            hoverBorderColor: '#ffffff',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        animation: false,
        cutout: `${cutoutPercentage}%`,
        layout: {
          padding: this.getResponsivePadding(),
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: !isEmptyState, // Désactiver les tooltips en mode empty
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const rawValue = context.raw as number;
                const dataset = context.dataset.data as number[];
                const total = dataset.reduce(
                  (a, b) => a + (typeof b === 'number' ? b : 0),
                  0,
                );
                const percentage =
                  total > 0 ? Math.round((rawValue / total) * 100) : 0;
                const valueWithUnit = this.formatCo2e.format(rawValue);
                const dataItem = this.data[context.dataIndex];
                const title = dataItem?.title;

                if (this.showLabelInTooltip) {
                  const displayLabel = title ? `${title} - ${label}` : label;
                  return `${displayLabel}: ${valueWithUnit} (${percentage}%)`;
                }
                return `${valueWithUnit} (${percentage}%)`;
              },
            },
          },
        },
        interaction: {
          intersect: true,
        },
        elements: {
          arc: {
            borderWidth: 0,
            hoverBorderWidth: 0,
          },
        },
      },
      plugins: [centerTextPlugin],
    };

    this.chart = new Chart(ctx, config);
  }

  // Méthode pour mettre à jour les données
  updateData(newData: ChartData[]): void {
    this.data = newData;
    if (!this.chart) {
      this.createChart();
      return;
    }
    if (this.chart) {
      // Check if all data is zero and replace with gray placeholder
      const hasNonZeroData = this.data.some((item) => item.value > 0);
      const isEmptyState = this.data.length === 0 || !hasNonZeroData;
      const chartData = isEmptyState
        ? [{ label: '', value: 100, color: this.colorsService.EMPTY_COLOR }]
        : this.data;

      this.chart.data.labels = chartData.map((item) => item.label);
      this.chart.data.datasets[0].data = chartData.map((item) => item.value);
      this.chart.data.datasets[0].backgroundColor = chartData.map(
        (item) => item.color,
      );

      // Update hover settings for empty state using type assertion
      const dataset = this.chart.data.datasets[0] as DoughnutDataset;
      dataset.hoverOffset = isEmptyState ? 0 : 8;
      dataset.hoverBorderWidth = isEmptyState ? 0 : 2;

      // Update tooltip enabled state
      if (this.chart.options?.plugins?.tooltip) {
        this.chart.options.plugins.tooltip.enabled = !isEmptyState;
      }

      this.chart.update();
    }
  }

  // Méthode pour mettre en évidence un segment
  highlightSegment(index: number): void {
    if (!this.chart || index < 0 || index >= this.data.length) {
      return;
    }

    // Utiliser l'API Chart.js pour activer un élément
    const activeElements = [
      {
        datasetIndex: 0,
        index: index,
      },
    ];

    this.chart.setActiveElements(activeElements);
    this.chart.update('none'); // Pas d'animation pour une réactivité instantanée
  }

  // Méthode pour retirer la mise en évidence
  clearHighlight(): void {
    if (!this.chart) {
      return;
    }

    this.chart.setActiveElements([]);
    this.chart.update('none');
  }
}
